<template>
  <div class="container grade-finalization-container">
    <el-card shadow="hover">
      <template #header>
        <div class="card-header">
          <h2>حساب واعتماد النتائج النهائية للفصل الدراسي</h2>
        </div>
      </template>

      <!-- --- الشرح الجديد هنا --- -->
      <el-collapse v-model="activeCollapse" style="margin-bottom: 20px;">
        <el-collapse-item name="1">
            <template #title>
                <el-icon><QuestionFilled /></el-icon><span style="margin-right: 8px; font-weight: bold;">لماذا هذه الواجهة مهمة؟ (اضغط للتوضيح)</span>
            </template>
            <div>
                <p>هذه الواجهة هي الخطوة الأخيرة في دورة الفصل الدراسي، وهي تقوم بتشغيل "المحرك" الحسابي للنظام.</p>
                <p><strong>ماذا يحدث عند الضغط على "حساب واعتماد النتائج"؟</strong></p>
                <ul>
                    <li>يقوم النظام بجمع كل الدرجات التي تم رصدها للطلاب في كل مادة (الواجبات، الاختبارات، المشاريع).</li>
                    <li>يطبق "وصفة" <strong>سياسة التقييم</strong> التي قمت بتعريفها مسبقًا (مثال: 40% للامتحان النهائي، 60% لأعمال الفصل).</li>
                    <li>يولد درجة نهائية معتمدة لكل طالب في كل مادة، ويخزنها بشكل دائم في قاعدة البيانات.</li>
                </ul>
                <p><strong>الأهمية:</strong> بدون إكمال هذه الخطوة، لا يمكن إصدار أي شهادات فصلية للطلاب أو حساب المعدل السنوي لاحقًا.</p>
            </div>
        </el-collapse-item>
      </el-collapse>

      <!-- فلاتر لاختيار السياق الأكاديمي -->
      <el-form label-position="top">
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="1. اختر السنة الدراسية">
              <el-select v-model="filters.study_year_id" placeholder="اختر السنة الدراسية" @change="handleFilterChange" clearable style="width: 100%;">
                <el-option v-for="year in studyYears" :key="year.id" :label="year.name" :value="year.id" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="2. اختر الفصل الدراسي">
              <el-select v-model="filters.term_id" placeholder="اختر الفصل الدراسي" @change="handleFilterChange" clearable :disabled="!filters.study_year_id" style="width: 100%;">
                <el-option v-for="term in terms" :key="term.id" :label="term.name" :value="term.id" />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>
      </el-form>
      
      <el-divider v-if="filters.study_year_id && filters.term_id" />

      <!-- جدول عرض حالة الصفوف -->
      <div v-if="filters.study_year_id && filters.term_id">
        <el-table :data="classStatuses" v-loading="loading" border style="width: 100%">
          <el-table-column prop="class_name" label="اسم الصف" />
          <el-table-column prop="student_count" label="عدد الطلاب" width="120" />
          <el-table-column label="حالة النتائج" width="250">
            <template #default="{ row }">
              <el-tag :type="getStatusTagType(row.status_code)">{{ row.status_text }}</el-tag>
            </template>
          </el-table-column>
          <el-table-column label="الإجراءات" width="300">
            <template #default="{ row }">
              <el-button
                :type="row.status_code === 'Finalized' ? 'warning' : 'primary'"
                :loading="row.is_calculating"
                :disabled="row.student_count === 0"
                @click="runGradeCalculation(row)"
              >
                <el-icon><Refresh /></el-icon> {{ row.status_code === 'Finalized' ? 'إعادة حساب النتائج' : 'حساب واعتماد النتائج' }}
              </el-button>
              <el-button
                type="success"
                :disabled="row.status_code !== 'Finalized'"
                @click="openPrintDialog(row)"
              >
                <el-icon><Printer /></el-icon> طباعة الشهادات
              </el-button>
            </template>
          </el-table-column>
        </el-table>
      </div>
      <div v-else>
        <el-empty description="الرجاء اختيار السنة والفصل الدراسي لعرض حالة الصفوف." />
      </div>

    </el-card>

    <!-- Dialog لاختيار قالب الطباعة -->
    <el-dialog v-model="printDialogVisible" title="طباعة الشهادات" width="40%">
      <div v-if="selectedClassForPrinting">
        <p>
          أنت على وشك طباعة شهادات لـ <strong>{{ selectedClassForPrinting.student_count }}</strong> طالب في صف
          <strong>{{ selectedClassForPrinting.class_name }}</strong>.
        </p>
        <el-form-item label="الرجاء اختيار قالب الشهادة المطلوب">
          <el-select v-model="selectedTemplateId" placeholder="اختر قالب" style="width: 100%;">
            <el-option v-for="template in templates" :key="template.id" :label="template.name" :value="template.id" />
          </el-select>
        </el-form-item>
      </div>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="printDialogVisible = false">إلغاء</el-button>
          <el-button type="primary" @click="handleGenerateAndPrint" :loading="isPrinting">
            <el-icon><Printer /></el-icon> عرض صفحة الطباعة
          </el-button>
        </span>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import axios from 'axios';
import { ElMessage, ElNotification } from 'element-plus';
import { Refresh, Printer, QuestionFilled } from '@element-plus/icons-vue';

export default {
  name: 'GradeFinalization',
  components: { Refresh, Printer, QuestionFilled },
  data() {
    return {
      api: null,
      loading: false,
      activeCollapse: '',
      filters: {
        study_year_id: null,
        term_id: null,
      },
      studyYears: [],
      terms: [],
      classStatuses: [],
      printDialogVisible: false,
      isPrinting: false,
      selectedClassForPrinting: null,
      templates: [],
      selectedTemplateId: null,
    };
  },
  created() {
    this.api = axios.create({ baseURL: process.env.VUE_APP_API_BASE_URL || 'https://cale.caledonian.ly/api' });
    this.api.interceptors.request.use((config) => {
      const token = localStorage.getItem('access_token');
      if (token) config.headers['Authorization'] = `Bearer ${token}`;
      return config;
    });
    this.fetchFilterData();
  },
  methods: {
    async fetchFilterData() {
      try {
        const [studyYearsRes, termsRes] = await Promise.all([
          this.api.get('/study-years'),
          this.api.get('/terms'),
        ]);
        this.studyYears = studyYearsRes.data.data || studyYearsRes.data;
        this.terms = termsRes.data.data || termsRes.data;
      } catch (e) {
        ElMessage.error('فشل في جلب بيانات الفلاتر.');
      }
    },
    handleFilterChange() {
      if (this.filters.study_year_id && this.filters.term_id) {
        this.fetchClassStatuses();
      } else {
        this.classStatuses = [];
      }
    },
    async fetchClassStatuses() {
      this.loading = true;
      try {
        const response = await this.api.get('/grade-calculation/statuses', { params: this.filters });
        this.classStatuses = response.data.map(status => ({ ...status, is_calculating: false }));
      } catch (error) {
        ElMessage.error(error.response?.data?.message || 'فشل في جلب حالة الصفوف.');
      } finally {
        this.loading = false;
      }
    },
    async runGradeCalculation(classRow) {
      classRow.is_calculating = true;
      try {
        const payload = {
          class_id: classRow.class_id,
          term_id: this.filters.term_id
        };
        const response = await this.api.post('/grade-calculation/run', payload);
        ElNotification({
          title: 'نجاح',
          message: response.data.message || 'تم حساب واعتماد النتائج بنجاح.',
          type: 'success',
        });
        this.fetchClassStatuses();
      } catch (error) {
        ElMessage.error(error.response?.data?.message || 'حدث خطأ أثناء حساب النتائج.');
      } finally {
        classRow.is_calculating = false;
      }
    },
    async openPrintDialog(classRow) {
      this.selectedClassForPrinting = classRow;
      await this.fetchReportCardTemplates();
      this.printDialogVisible = true;
    },
    async fetchReportCardTemplates() {
      try {
        const response = await this.api.get('/report-card-templates');
        this.templates = response.data;
        if (this.templates.length > 0) {
          this.selectedTemplateId = this.templates[0].id;
        }
      } catch (error) {
        ElMessage.error('فشل في جلب قوالب الشهادات.');
      }
    },
    handleGenerateAndPrint() {
      if (!this.selectedTemplateId) {
        ElMessage.warning('الرجاء اختيار قالب للطباعة.');
        return;
      }
      const routeData = this.$router.resolve({
        name: 'ReportCardViewer',
        query: { 
          class_id: this.selectedClassForPrinting.class_id,
          term_id: this.filters.term_id,
          template_id: this.selectedTemplateId
        }
      });
      window.open(routeData.href, '_blank');
      this.printDialogVisible = false;
    },
    getStatusTagType(statusCode) {
      if (statusCode === 'Finalized') return 'success';
      if (statusCode === 'Pending') return 'warning';
      return 'info';
    },
  },
};
</script>

<style scoped>
.grade-finalization-container { padding: 20px; }
.card-header { display: flex; justify-content: space-between; align-items: center; }
.filter-section { display: flex; gap: 15px; margin-bottom: 20px; flex-wrap: wrap; }
.pagination-container { display: flex; justify-content: center; margin-top: 20px; }
</style>
